import {
  DisplayMarketingAd,
  PublisherPlatforms,
  DisplayMarketingCampaign,
  DisplayMarketingAdSet,
  UpdateAdCreativeRequest,
  RatioType,
  AdFormat,
  CallToActionType,
  FacebookPositions,
  InstagramPositions,
  RatioPositions,
} from "common/interfaces/KYDMarketing";
import { AdCreativeFormData } from "../forms/adcreative";
import { CallToActionOption } from "src/utils/callToActionOptions";

const input = ({
  callToActionOptions,
  ad,
  campaign,
  adset,
  link,
  pages,
  instagramAccounts,
}: {
  callToActionOptions: CallToActionOption[];
  ad?: DisplayMarketingAd;
  link?: string;
  campaign: DisplayMarketingCampaign;
  adset: DisplayMarketingAdSet;
  pages: {
    id: string;
    name: string;
  }[];
  instagramAccounts: {
    id: string;
    username: string;
  }[];
}): AdCreativeFormData => {
  // Determine media type based on creative data
  let mediaType: "carousel" | "single" = "single";
  let carouselMediaType = null;

  // Check if there are multiple photos of the same type or multiple videos of the same type
  const portraitImages =
    ad?.creative?.assets?.images?.filter(
      (img) => img.src && img.type === "portrait"
    ) || [];
  const squareImages =
    ad?.creative?.assets?.images?.filter(
      (img) => img.src && img.type === "square"
    ) || [];
  const portraitVideos =
    ad?.creative?.assets?.videos?.filter(
      (video) => video.src && video.type === "portrait"
    ) || [];
  const squareVideos =
    ad?.creative?.assets?.videos?.filter(
      (video) => video.src && video.type === "square"
    ) || [];

  // If there are multiple images of the same type (portrait or square) or multiple videos of the same type
  if (
    portraitImages.length > 1 ||
    squareImages.length > 1 ||
    portraitVideos.length > 1 ||
    squareVideos.length > 1
  ) {
    mediaType = "carousel";

    // Determine carousel media type based on what's available
    if (portraitImages.length > 0 || squareImages.length > 0) {
      carouselMediaType = "photo";
    } else if (portraitVideos.length > 0 || squareVideos.length > 0) {
      carouselMediaType = "video";
    }
  }

  const callToAction = ad?.creative?.assets?.call_to_actions?.[0]?.type;

  const callToActionValue =
    callToActionOptions.find((cta) => cta.value === callToAction) || null;

  const defaultCallToAction = callToActionOptions.find(
    (cta) => cta.value === CallToActionType.ORDER_NOW
  );

  const defaultFacebookPlacements =
    RatioPositions[RatioType.portrait][PublisherPlatforms.facebook];

  const defaultInstagramPlacements =
    RatioPositions[RatioType.portrait][PublisherPlatforms.instagram];

  const customizationRules = ad?.creative?.customization_rules || [];

  const hasFacebookPositions = (
    rule: (typeof customizationRules)[number]
  ): rule is Extract<
    (typeof customizationRules)[number],
    { customization: { facebook_positions?: FacebookPositions[] } }
  > => "facebook_positions" in rule.customization;

  const hasInstagramPositions = (
    rule: (typeof customizationRules)[number]
  ): rule is Extract<
    (typeof customizationRules)[number],
    { customization: { instagram_positions?: InstagramPositions[] } }
  > => "instagram_positions" in rule.customization;

  const facebookRulePlacements = customizationRules
    .filter(hasFacebookPositions)
    .flatMap((rule) => rule.customization.facebook_positions || [])
    .filter(Boolean) as FacebookPositions[];

  const instagramRulePlacements = customizationRules
    .filter(hasInstagramPositions)
    .flatMap((rule) => rule.customization.instagram_positions || [])
    .filter(Boolean) as InstagramPositions[];

  // Prefer explicit placements when rules exist.
  // Only fall back to defaults when the ad is new (no id) and there are no rules.
  const isNewAd = !ad?.id;
  const facebookPlacements =
    customizationRules.length > 0
      ? facebookRulePlacements
      : isNewAd
      ? defaultFacebookPlacements
      : [];
  const instagramPlacements =
    customizationRules.length > 0
      ? instagramRulePlacements
      : isNewAd
      ? defaultInstagramPlacements
      : [];

  return {
    id: ad?.id || "",
    name: ad?.name || "Creative",
    external_id: ad?.external_id || "",
    status: adset?.status,
    adset_id: adset?.id,
    campaign_id: campaign?.id,
    organization_id: campaign?.organization_id,
    website_url: ad?.creative?.assets?.links?.[0]?.url,
    call_to_action: callToActionValue
      ? callToActionValue
      : {
          value: defaultCallToAction?.value,
          label: defaultCallToAction?.label,
        },

    facebook_placements: facebookPlacements.flat(),
    instagram_placements: instagramPlacements.flat(),

    // Creative properties
    ad_formats: ad?.creative?.ad_formats || [],

    // Media type
    media_type: mediaType,
    carousel_media_type: carouselMediaType,

    // Assets - map from interface to form structure
    videos: {
      portrait:
        portraitVideos.map((video) => ({
          fileKey: video.file_key || null,
          source: video.src || null,
          thumbnail_url: video.thumbnail_src || null,
        })) || [],
      square:
        squareVideos.map((video) => ({
          fileKey: video.file_key || null,
          source: video.src || null,
          thumbnail_url: video.thumbnail_src || null,
        })) || [],
    },
    images: {
      portrait:
        portraitImages.map((img) => ({
          fileKey: img.file_key || null,
          picture_url: img.src || null,
        })) || [],
      square:
        squareImages.map((img) => ({
          fileKey: img.file_key || null,
          picture_url: img.src || null,
        })) || [],
    },

    // Form-specific properties
    page_id: adset?.identity?.[PublisherPlatforms.facebook]?.page_id
      ? {
          value: adset?.identity?.[PublisherPlatforms.facebook]?.page_id,
          label: `${
            pages.find(
              (p) =>
                p.id === adset?.identity?.[PublisherPlatforms.facebook]?.page_id
            )?.name
          }`,
        }
      : {
          value: pages.at(0)?.id || "",
          label: pages.at(0)?.name || "",
        },
    instagram_actor_id: adset?.identity?.[PublisherPlatforms.instagram]
      ?.instagram_actor_id
      ? {
          value:
            adset?.identity?.[PublisherPlatforms.instagram]?.instagram_actor_id,
          label: `${
            instagramAccounts.find(
              (p) =>
                p.id ===
                adset?.identity?.[PublisherPlatforms.instagram]
                  ?.instagram_actor_id
            )?.username
          }`,
        }
      : {
          value: instagramAccounts.at(0)?.id || "",
          label: instagramAccounts.at(0)?.username || "",
        },
    message: ad?.creative?.assets?.bodies?.[0]?.text || "",
  };
};

/**
 * Serializes form data to UpdateAdCreativeRequest format
 */
const output = ({
  data,
  adset,
  campaign,
  pages,
  instagramAccounts,
  callToActionOptions,
}: {
  data: AdCreativeFormData;
  adset: DisplayMarketingAdSet;
  campaign: DisplayMarketingCampaign;
  pages: {
    id: string;
    name: string;
  }[];
  instagramAccounts: {
    id: string;
    username: string;
  }[];
  callToActionOptions: CallToActionOption[];
}): UpdateAdCreativeRequest => {
  // Handle different media types with type preservation
  let flattenedImages: Array<{ item: any; type: string }> = [];
  let flattenedVideos: Array<{ item: any; type: string }> = [];

  const otherAds = adset.ads
    .filter((ad) => ad.id !== data.id)
    .map((ad) => {
      return input({
        ad,
        campaign,
        adset,
        pages,
        instagramAccounts,
        callToActionOptions,
      });
    });

  // For photo type, use portrait and square images with their respective types
  const portraitImages = (data.images?.portrait || []).map((item) => ({
    item,
    type: "portrait",
  }));
  const squareImages = (data.images?.square || []).map((item) => ({
    item,
    type: "square",
  }));
  flattenedImages = [...portraitImages, ...squareImages];
  const portraitVideos = (data.videos?.portrait || []).map((item) => ({
    item,
    type: "portrait",
  }));
  const squareVideos = (data.videos?.square || []).map((item) => ({
    item,
    type: "square",
  }));
  flattenedVideos = [...portraitVideos, ...squareVideos];

  const mapAdData = (
    data: AdCreativeFormData,
    adset: DisplayMarketingAdSet,
    campaign: DisplayMarketingCampaign,
    flattenedImages: Array<{ item: any; type: string }>,
    flattenedVideos: Array<{ item: any; type: string }>
  ) => {
    return {
      id: data.id || undefined,
      external_id: data.external_id || undefined,
      campaign_id: campaign.id,
      organization_id: campaign.organization_id,
      status: data.status,
      parent_id: adset.id,
      ad_formats:
        data.media_type === "carousel"
          ? [AdFormat.CAROUSEL]
          : (() => {
              const hasImages =
                (data.images?.portrait?.length || 0) +
                  (data.images?.square?.length || 0) >
                0;
              const hasVideos =
                (data.videos?.portrait?.length || 0) +
                  (data.videos?.square?.length || 0) >
                0;

              if (hasImages && hasVideos) {
                return [AdFormat.SINGLE_IMAGE, AdFormat.SINGLE_VIDEO];
              } else if (hasImages) {
                return [AdFormat.SINGLE_IMAGE];
              } else if (hasVideos) {
                return [AdFormat.SINGLE_VIDEO];
              } else {
                return [AdFormat.SINGLE_IMAGE]; // Default fallback
              }
            })(),
      creative: {
        name: data.name,
        primary_text: data.message,
        call_to_action: data.call_to_action?.value || "LEARN_MORE", // Default CTA
        link: data.website_url || "",
        facebook_placements: (data.facebook_placements ||
          []) as FacebookPositions[],
        instagram_placements: (data.instagram_placements ||
          []) as InstagramPositions[],
        media: {
          images: flattenedImages.map((img) => ({
            ...(img.item.fileKey ? {} : { src: img.item.picture_url || "" }),
            type: img.type as RatioType,
            hash: null,
            file_key: img.item.fileKey || null,
          })),
          videos: flattenedVideos.map((video) => ({
            ...(video.item.fileKey ? {} : { src: video.item.source || "" }),
            thumbnail_src: video.item.thumbnail_url || "",
            type: video.type as RatioType,
            external_id: null,
            file_key: video.item.fileKey || null,
          })),
        },
      },
    };
  };

  return {
    organization_id: campaign.organization_id,
    campaign_id: campaign.id,
    parent_id: adset.id,
    identity: {
      [PublisherPlatforms.facebook]: {
        page_id: data.page_id?.value || "",
      },
      [PublisherPlatforms.instagram]: {
        instagram_actor_id: data.instagram_actor_id?.value || "",
      },
    },
    ads: [
      ...otherAds.map((ad) => {
        // Create flattened media arrays for each existing ad using their own data
        const existingAdPortraitImages = (ad.images?.portrait || []).map(
          (item) => ({
            item,
            type: "portrait",
          })
        );
        const existingAdSquareImages = (ad.images?.square || []).map(
          (item) => ({
            item,
            type: "square",
          })
        );
        const existingAdFlattenedImages = [
          ...existingAdPortraitImages,
          ...existingAdSquareImages,
        ];

        const existingAdPortraitVideos = (ad.videos?.portrait || []).map(
          (item) => ({
            item,
            type: "portrait",
          })
        );
        const existingAdSquareVideos = (ad.videos?.square || []).map(
          (item) => ({
            item,
            type: "square",
          })
        );
        const existingAdFlattenedVideos = [
          ...existingAdPortraitVideos,
          ...existingAdSquareVideos,
        ];

        return mapAdData(
          ad,
          adset,
          campaign,
          existingAdFlattenedImages,
          existingAdFlattenedVideos
        );
      }),
      mapAdData(data, adset, campaign, flattenedImages, flattenedVideos),
    ],
  };
};

const transform = {
  input,
  output,
};

export default transform;
